import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { SearchService } from '../../services/search.service';
import type { SearchResult } from 'utils/interfaces/search.util';

interface ViewAllOption {
  readonly kind: 'viewAll';
  readonly query: string;
}

type AutocompleteOption = SearchResult | ViewAllOption;

@Component({
  selector: 'gcp-workspace-search-bar',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent {
  private readonly searchService = inject(SearchService);
  private readonly router = inject(Router);

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly typeaheadResults$ = this.searchService.typeaheadResults$;

  constructor() {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value.length >= 2) {
          this.searchService.typeahead(value);
        }
      });
  }

  onSearch(): void {
    const query = this.searchControl.value.trim();
    if (query.length < 2) return;
    this.goToResultsPage(query);
  }

  buildViewAllOption(rawQuery: string): ViewAllOption {
    return { kind: 'viewAll', query: rawQuery.trim() };
  }

  displayResultTitle(option: AutocompleteOption | string | null): string {
    if (!option || typeof option === 'string') return '';
    if ('kind' in option) return '';
    return option.title ?? '';
  }

  onOptionSelected(option: AutocompleteOption): void {
    if ('kind' in option) {
      this.goToResultsPage(option.query);
      return;
    }
    this.navigateToResult(option);
  }

  private navigateToResult(result: SearchResult): void {
    const entityType = result.entityType?.toLowerCase();
    const issueKey = result.metadata?.['issue_key'];
    const auditId = result.metadata?.['audit_id'];

    if (entityType === 'issue' && issueKey) {
      void this.router.navigate(['/issue', issueKey]);
    } else if (entityType === 'audit' && auditId) {
      void this.router.navigate(['/audit', auditId]);
    } else if (entityType === 'attachment') {
      const filename = result.metadata?.['filename'] ?? result.title;
      if (filename) {
        const filterParam = encodeURIComponent(
          JSON.stringify({
            filterType: 'text',
            type: 'equals',
            filter: filename,
          }),
        );
        void this.router.navigate(['/attachments'], {
          queryParams: { name: filterParam },
        });
      }
    } else if (issueKey) {
      void this.router.navigate(['/issue', issueKey]);
    } else if (auditId) {
      void this.router.navigate(['/audit', auditId]);
    }

    this.searchControl.setValue('');
  }

  private goToResultsPage(query: string): void {
    if (query.length < 2) return;
    void this.router.navigate(['/search'], {
      queryParams: { q: query, page: 0 },
      queryParamsHandling: 'merge',
    });
    this.searchControl.setValue('');
  }
}
